import {
  CustomerShortDto,
  ProcessDto,
  ProcessOfferItemDto,
  ProcessStatusDto,
} from '@/api/generated';
import {
  CustomerTypeOptionsTranslation,
  customerTypeOptionsTranslation,
  processOfferStatusOptionsTranslation,
  ProcessOfferStatusOptionsTranslation,
  processStatusOptionsTranslation,
  ProcessStatusTranslation,
} from '@/assets/locales/utils/dictionaries/dictionaries.translation.pl.ts';
import { ProcessOffersFormModel } from '@/views/processesDetails/components/offers/model/ProcessOffersForm.model.ts';
import { ProcessDetailsTab } from '@/views/processesDetails/model/ProcessDetailsTab.enum.ts';

export type ProcessesDetailsTranslation = {
  addTask: {
    button: {
      open: string;
    };
  };
  customer: Record<keyof Omit<CustomerShortDto, 'customerId'>, string> & {
    changeConfirm: {
      cancel: string;
      confirm: string;
      content: string;
      title: string;
    };
    noCustomerSelected: string;
    tooltip: {
      goToCustomer: string;
    };
    typeOptions: CustomerTypeOptionsTranslation;
  };
  header: {
    button: {
      addMultiOffer: string;
      edit: string;
    };
    menu: {
      offerWizard: string;
      offerWizardHint: string;
      quickOffer: string;
      quickOfferHint: string;
    };
    nextStatus: ProcessStatusTranslation;
    status: string;
    statusOptions: ProcessStatusTranslation;
  };
  main: Record<
    keyof Omit<ProcessDto, 'customer' | 'description' | 'name' | 'processId'>,
    string
  > & {
    eventTypeHint: string;
    inquirySourceCreatorOnly: string;
    noEventType: string;
  };
  offers: {
    body: Record<
      keyof Omit<
        ProcessOfferItemDto,
        | 'availabilityRules'
        | 'calendarVisibility'
        | 'offerPrintId'
        | 'offerPrintName'
        | 'predefinedOffer'
        | 'processId'
        | 'processMultiOfferId'
        | 'processOfferId'
        | 'versionCorrelationId'
        | 'versionNumber'
      >,
      string
    > & {
      version: string;
    };
    button: {
      createOffer: string;
    };
    head: Record<keyof ProcessOffersFormModel, string> & {
      statusOptions: ProcessOfferStatusOptionsTranslation;
    };
    noCustomerTooltip: string;
    title: string;
  };
  processContacts: {
    button: {
      cancel: string;
      submit: string;
    };
    contactOwner: string;
    noContactOwner: string;
    title: string;
    tooltip: {
      goToContact: string;
      manageContacts: string;
    };
  };
  tabs: Record<ProcessDetailsTab, string>;
  tasks: {
    button: {
      markMessagesAsSeen: string;
    };
    dialog: {
      markMessagesAsSeen: {
        button: {
          cancel: string;
          confirm: string;
        };
        content: string;
        title: string;
      };
    };
    title: string;
  };
  toDoInfo: {
    allGood: string;
    requiresAttention: string;
  };
};

export const processesDetailsTranslation: ProcessesDetailsTranslation = {
  addTask: {
    button: {
      open: 'Dodaj zadanie',
    },
  },
  customer: {
    changeConfirm: {
      cancel: 'Anuluj',
      confirm: 'Zmień klienta',
      content:
        'Klient jest wspólny dla całego procesu — zmiana obejmie wszystkie jego oferty, także te ' +
        'już wysłane. Obecny klient: {{current}}.',
      title: 'Zmienić klienta procesu?',
    },
    name: 'Klient',
    noCustomerSelected: 'Nie przypisano klienta do tego procesu',
    tooltip: {
      goToCustomer: 'Przejdź do klienta',
    },
    type: 'Typ',
    typeOptions: customerTypeOptionsTranslation,
  },
  header: {
    button: {
      addMultiOffer: 'Dodaj ofertę',
      edit: 'Edycja',
    },
    menu: {
      offerWizard: 'Kreator oferty',
      offerWizardHint: 'Pełny formularz grupy ofert',
      quickOffer: 'Szybka oferta',
      quickOfferHint: 'Termin, goście — i od razu układasz menu',
    },
    nextStatus: {
      [ProcessStatusDto.Canceled]: 'Anuluj',
      [ProcessStatusDto.Created]: 'Utwórz',
      [ProcessStatusDto.Finished]: 'Zakończ',
      [ProcessStatusDto.InProgress]: 'Rozpocznij',
    },
    status: 'Status',
    statusOptions: processStatusOptionsTranslation,
  },
  main: {
    createdAt: 'Utworzony',
    customerContacts: 'Kontakty klienta',
    eventType: 'Typ wydarzenia',
    eventTypeHint:
      'Podpowiada tagi dań i szablon startowy oferty. Zmiana nie przebudowuje istniejących menu.',
    inquirySource: 'Źródło zapytania',
    inquirySourceCreatorOnly:
      'Źródło zapytania może zmienić wyłącznie osoba, która założyła proces.',
    noEventType: 'Brak',
    processCreator: 'Twórca procesu',
    processOwner: 'Właściciel procesu',
    status: 'Status',
    updatedAt: 'Ostatnia aktualizacja',
  },
  offers: {
    body: {
      createdAt: 'Data utworzenia',
      creator: 'Autor',
      eventDateFrom: 'Start',
      eventDateTo: 'Koniec',
      name: 'Nazwa',
      noOfGuests: 'Liczba gości',
      sentAt: 'Wysłana',
      status: 'Status',
      version: 'Wersja',
    },
    button: {
      createOffer: 'Utwórz ofertę',
    },
    head: {
      statuses: 'Statusy',
      statusOptions: processOfferStatusOptionsTranslation,
    },
    noCustomerTooltip: 'Nie można utworzyć oferty bez zdefiniowanego klienta',
    title: 'Oferty',
  },
  processContacts: {
    button: {
      cancel: 'Anuluj',
      submit: 'Zapisz',
    },
    contactOwner: 'Opiekun kontaktu',
    noContactOwner: 'Brak opiekuna',
    title: 'Osoby do kontaktu',
    tooltip: {
      goToContact: 'Przejdź do kontaktu',
      manageContacts: 'Wybierz osoby do kontaktu',
    },
  },
  tabs: {
    [ProcessDetailsTab.FILES]: 'Pliki',
    [ProcessDetailsTab.MESSAGES]: 'Maile i notatki',
    [ProcessDetailsTab.MULTI_OFFERS]: 'Grupy oferty',
    [ProcessDetailsTab.TASKS]: 'Zadania',
  },
  tasks: {
    button: {
      markMessagesAsSeen: 'Potwierdź przeczytanie wiadomości',
    },
    dialog: {
      markMessagesAsSeen: {
        button: {
          cancel: 'Anuluj',
          confirm: 'Potwierdź',
        },
        content:
          'Potwierdzenie tej akcji spowoduje zamknięcie wszystkich zadań w procesie dotyczących wiadomości.',
        title: 'Potwierdź przeczytanie wiadomości',
      },
    },
    title: 'Zadania',
  },
  toDoInfo: {
    allGood: 'Wszystko ok',
    requiresAttention: 'Proces wymaga uwagi',
  },
};
